import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { View } from 'react-native';
import { useAppSelector } from 'store/hooks';
import { AppView } from 'design-system';
import AmountSelectors from 'design-system/AmountSelectors';
import Button from 'design-system/Button';
import MoneyInput from 'design-system/MoneyInput';
import Spacer from 'design-system/Spacer';
import StatusBar from 'design-system/StatusBar';
import { rem } from 'design-system/values';
import { useFetchOld } from 'hooks';
import useColors from 'hooks/useColors';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { HELP_ARTICLES_KEY } from 'features/help/constants';
import { editSavingsPlan } from 'features/pots/actions';
import { AUTOSAVE_DEFAULT_MONTHLY_AMOUNT, AUTOSAVE_DEFAULT_WEEKLY_AMOUNT, AUTOSAVE_MIN_AMOUNT, AUTOSAVE_MONTHLY_INVESTMENT_AMOUNTS, AUTOSAVE_WEEKLY_INVESTMENT_AMOUNTS, } from 'features/pots/constants';
import { selectAutosaveRulesArr } from 'features/pots/reducers/selectors';
import { SavingsPlanRuleType } from 'features/pots/types';
import createStyleSheets from 'utils/createStyleSheets';
const fetchOptions = {
    throws: false,
    muteError: true,
    muteResult: true,
    muteFetching: false,
};
const AutosaveChooseCustomAmount = ({ route, navigation }) => {
    const colors = useColors();
    const styles = useStyles(styleSet);
    const [, , fetching, fetch] = useFetchOld(fetchOptions);
    const { isMonthly, initialAmount } = route.params;
    const savingsPlanId = useAppSelector((state) => state.pots.savingsPlan?.id);
    const autosaveRulesArr = useAppSelector(selectAutosaveRulesArr);
    const moneyInputRef = useRef(null);
    const defaultAmount = initialAmount || (isMonthly ? AUTOSAVE_DEFAULT_MONTHLY_AMOUNT : AUTOSAVE_DEFAULT_WEEKLY_AMOUNT);
    useLayoutEffect(() => {
        navigation.setOptions({
            title: isMonthly ? 'Choose monthly deposit' : 'Choose weekly deposit',
            headerTintColor: colors.text.primary,
            headerRight: () => <HelpScreenButton articlesKey={HELP_ARTICLES_KEY.AUTOSAVE_ONBOARDING}/>,
        });
    });
    const [amount, setAmount] = useState(0);
    const moneyInputColors = useMemo(() => ({
        input: colors.text.primary,
        pinpad: colors.text.primary,
        hintColor: colors.text.statusNegative,
    }), [colors.text.primary, colors.text.statusNegative]);
    const paddingBottom = useMarginBottom('paddingBottom', undefined, styles.container);
    const onAmountChange = useCallback((value) => {
        setAmount(value);
    }, []);
    const numPadHeader = useMemo(() => (<View style={styles.numPadHeader}>
        <Spacer h={24}/>
        <AmountSelectors amounts={isMonthly ? AUTOSAVE_MONTHLY_INVESTMENT_AMOUNTS : AUTOSAVE_WEEKLY_INVESTMENT_AMOUNTS} moneyInputRef={moneyInputRef}/>
      </View>), [isMonthly, styles.numPadHeader]);
    const onSubmit = useCallback(async () => {
        if (route.params?.onContinue) {
            route.params?.onContinue(amount);
            navigation.goBack();
            return;
        }
        if (amount >= AUTOSAVE_MIN_AMOUNT) {
            if (savingsPlanId) {
                const newRules = autosaveRulesArr.map((rule) => {
                    if (rule.type === SavingsPlanRuleType.CUSTOM) {
                        return {
                            ...rule,
                            amount: { value: amount, currency: 'GBP' },
                        };
                    }
                    return rule;
                });
                await fetch(editSavingsPlan(savingsPlanId, { rules: newRules }));
                navigation.goBack();
            }
        }
        else {
            moneyInputRef.current?.shake();
        }
    }, [amount, autosaveRulesArr, route.params, savingsPlanId]);
    return (<AppView type="primary" avoidHeader>
      <View style={paddingBottom}>
        <StatusBar barStyle="dark-content" animated/>
        <MoneyInput flex showMinHint showMaxHint ref={moneyInputRef} min={AUTOSAVE_MIN_AMOUNT} colors={moneyInputColors} pinPadStyle={styles.pinPad} onValueChange={onAmountChange} numPadHeader={numPadHeader} initialValue={defaultAmount} max={isMonthly ? 1500 : 400} alwaysDisplayMaxHint={false} alwaysDisplayMinHint={false}/>
        <Spacer h={30}/>
        <View style={styles.buttonContainer}>
          <Button brand onPress={onSubmit} loading={fetching} disabled={amount < AUTOSAVE_MIN_AMOUNT} title={route.params?.onContinue ? 'Continue' : 'Save'}/>
        </View>
      </View>
    </AppView>);
};
const styleSet = createStyleSheets(() => ({
    container: {
        flex: 1,
    },
    buttonContainer: {
        paddingHorizontal: rem(16),
    },
    withdrawInfoCardContainer: {
        width: '100%',
        marginTop: rem(30),
    },
    pinPad: {
        marginTop: rem(10),
    },
    numpadHeader: {
        flex: 2,
    },
    row: {
        alignItems: 'center',
        flexDirection: 'row',
        justifyContent: 'center',
    },
    numPadHeader: {
        flex: 2,
        width: '100%',
    },
}));
export default AutosaveChooseCustomAmount;
